import Stage from './Stage'
import Funnel from '@/components/funnel'
import { useMutation, useQuery } from '@apollo/client'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { showNotification } from '@/utils/notifications'
import { CubeLoader } from '@/components/ui/cube-loader'
import { GET_AUDIENCES } from '@/graphql/audience-queries'
import { GET_AUDIENCE_BUILDER_ENABLED } from '@/graphql/audience-hypothesis-queries'
import { ADD_AUDIENCE_ELIGIBILITY, REMOVE_UNFINISHED_AD_SETS } from '@/graphql/add-audience-queries'
import { Button } from '@/components/ui/button'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { useAudiencesWithHypotheses } from '@/hooks/useAudiencesWithHypotheses'
import {
    buildSetInputs,
    getBuilderState,
    getHypothesisMap,
    isAiAudienceObjective,
    isSetStale,
    parseBudgetAmount,
} from '@/utils/audience-builder'
import AiAudiencePanel from './AiAudiencePanel'
import WarmAudiencePanel from './WarmAudiencePanel'
import LookalikePanel from './LookalikePanel'
import AccountAudiencesPanel from './AccountAudiencesPanel'
import { getLookalikeMap } from '@/utils/lookalike-audiences'
import { getBudgetMove, withDonorInput } from '@/utils/budget-move'
import { useWizard } from '@/components/common/wizard/WizardContext'
import { getAvailableStages, getAllStages, isAudienceExcluded } from '@/utils/ad-campaigns-helpers'
import { WARM_AUDIENCE_ID } from '@/utils/warm-audiences'
import { isAccountAudienceId } from '@/utils/account-audiences'
import { EXCLUDED_AUDIENCES, EXCLUSION_REASONS } from '@/utils/ad-campaign-constants'
import { Tour } from '@/components/Tour'
import { ExternalLinkIcon } from '@/components/icons'

const EMPTY_AUDIENCES = []

// Shown on an existing campaign when AI audiences are not offered for it. New ad sets left over by a budget move that
// did not finish are listed with a way to remove them.
const AddAudienceNotOffered = ({ message, unfinishedAdSets = [], onRemove, removing = false }) => (
    <div
        className="tw:mx-6 tw:flex tw:flex-col tw:gap-1 tw:rounded-lg tw:border tw:p-4"
        data-testid="ai-audience-not-offered"
    >
        <span className="tw:text-sm tw:font-semibold">Turba AI Audience</span>
        <p className="tw:text-sm tw:text-gray-200" role="status">
            {message}
        </p>
        {unfinishedAdSets.length > 0 && (
            <>
                <ul className="tw:list-disc tw:pl-5 tw:text-sm" data-testid="unfinished-ad-sets">
                    {unfinishedAdSets.map((adSet) => (
                        <li key={adSet.ad_set_id}>{adSet.name}</li>
                    ))}
                </ul>
                <Button variant="secondary" size="sm" className="tw:w-fit" disabled={removing} onClick={onRemove}>
                    Remove these ad sets
                </Button>
            </>
        )}
    </div>
)

const TourFunneStep = ({ text }) => {
    return (
        <div className="tw:text-sm">
            <div className="tw:leading-[21px]">{text}</div>
            <div className="tw:flex tw:flex-col tw:gap-1 tw:mt-4">
                <span className="tw:text-sm tw:font-bold">Learn more</span>
                <a
                    href="https://www.facebook.com/business/ads/ad-objectives"
                    target="_blank"
                    className="tw:text-blue-300 tw:text-sm tw:font-semibold tw:flex tw:gap-1 tw:items-center"
                >
                    <ExternalLinkIcon /> Ad objectives
                </a>
                <a
                    href="https://www.facebook.com/business/help/1438417719786914"
                    target="_blank"
                    className="tw:text-blue-300 tw:text-sm tw:font-semibold tw:flex tw:gap-1 tw:items-start"
                >
                    <ExternalLinkIcon className="tw:min-h-6 tw:min-w-6" /> How to choose the right ad objective in Meta
                    Ads Manager
                </a>
            </div>
        </div>
    )
}

const SalesFunnelSetup = () => {
    const {
        getStepContentData,
        setStepContentData,
        removeStepContentData,
        registerNextGuard,
        deregisterNextGuard,
        showGuidePopups,
    } = useWizard()
    const { id: campaignId, ad_campaign_id: addAudienceCampaignId } = useParams()
    const { connectedPublishers } = useMediaBuyingContext()

    const { data: audiencesData, loading: audiencesLoading } = useQuery(GET_AUDIENCES)

    // The AI audience option is only offered when the licence has it enabled. A failed check leaves the screen as it was.
    const { data: builderFlagData, error: builderFlagError } = useQuery(GET_AUDIENCE_BUILDER_ENABLED, {
        variables: { campaignId },
        skip: !campaignId || !!addAudienceCampaignId,
    })
    // On an existing campaign the server decides whether AI audiences can be added, funded by budget moved from one of its ad sets
    const {
        data: eligibilityData,
        error: eligibilityError,
        refetch: refetchEligibility,
    } = useQuery(ADD_AUDIENCE_ELIGIBILITY, {
        variables: {
            adCampaignId: addAudienceCampaignId,
            publisherId: connectedPublishers ? Object.keys(connectedPublishers)[0] : undefined,
        },
        skip: !addAudienceCampaignId || !connectedPublishers || Object.keys(connectedPublishers).length === 0,
        fetchPolicy: 'network-only',
    })
    const addAudienceEligibility = eligibilityError ? null : eligibilityData?.AudienceHypothesis?.addAudienceEligibility
    // Unpublished ad sets of a budget move that did not finish; their budget never left the ad set it was to come from
    const unfinishedAdSets = addAudienceEligibility?.unfinished_ad_sets || []
    const [removeUnfinished] = useMutation(REMOVE_UNFINISHED_AD_SETS)
    const [removingUnfinished, setRemovingUnfinished] = useState(false)
    // Removed in one request that never touches Meta; ad sets published meanwhile are left as they are and named
    const removeUnfinishedAdSets = async () => {
        setRemovingUnfinished(true)
        try {
            const { data } = await removeUnfinished({
                variables: {
                    adCampaignId: addAudienceCampaignId,
                    adSetIds: unfinishedAdSets.map((adSet) => adSet.ad_set_id),
                },
            })
            const result = data?.removeUnfinishedAdSets
            if (result?.kept?.length > 0) {
                showNotification(result.message || 'Some ad sets were not removed.', 'info')
            } else {
                showNotification('The unpublished ad sets were removed.', 'success')
            }
        } catch (error) {
            showNotification(
                error?.graphQLErrors?.[0]?.message || 'The ad sets could not be removed. Try again.',
                'error',
            )
        } finally {
            setRemovingUnfinished(false)
            await refetchEligibility()
        }
    }
    const aiAudienceEnabled = addAudienceCampaignId
        ? addAudienceEligibility?.eligible === true
        : !builderFlagError && builderFlagData?.AudienceHypothesis?.audienceBuilderEnabled === true

    const selectFormatData = getStepContentData('campaignObjective') || {}
    const selectedObjective = selectFormatData.selectedObjective?.toLowerCase() ?? 'awareness'

    // Get selected audience IDs from wizard state
    const salesFunnelData = getStepContentData('salesFunnelSetup') || {}
    const [audienceSelections, setAudienceSelections] = useState(salesFunnelData.audienceSelections || {})

    const availableStages = getAvailableStages(selectedObjective)
    const pixelIsActive = !!selectFormatData?.pixelIsValid
    const allStages = getAllStages()
    const catalogueAudiences = audiencesData?.Audience?.audiences
    const audiences = useAudiencesWithHypotheses(catalogueAudiences || EMPTY_AUDIENCES, getStepContentData)

    // Filter available stages to exclude those with no available audiences due to pixel restrictions
    const getFilteredAvailableStages = () => {
        if (pixelIsActive) return availableStages

        const excludedAudiences = EXCLUDED_AUDIENCES[EXCLUSION_REASONS.invalidPixel] || []

        return availableStages.filter((stageId) => {
            // Check if this stage has any non-excluded audiences
            const stageAudiences = audiences.filter((audience) => audience.funnel_stage === stageId)
            // The lead form retargeting option and audiences of the ad account do not need the pixel, so a stage that carries one stays
            if ((audienceSelections[stageId] || []).some((id) => id === WARM_AUDIENCE_ID || isAccountAudienceId(id)))
                return true
            return stageAudiences.some((audience) => !excludedAudiences.includes(audience.funnel_stage))
        })
    }

    const filteredAvailableStages = getFilteredAvailableStages()

    // Helper to get selected audiences for a stage
    const getSelectedAudiences = (stageId) => {
        const selectedIds = audienceSelections[stageId] || []
        const baseAudienceList = audiences.filter((audience) => selectedIds.includes(audience.id))
        if (!pixelIsActive) {
            // If pixel is not active, filter out exclusions
            return baseAudienceList.filter((audience) => !isAudienceExcluded(audience, pixelIsActive))
        }
        return baseAudienceList
    }

    const builderState = getBuilderState(salesFunnelData)
    const hypothesisMap = getHypothesisMap(salesFunnelData)
    // Not offered for some objectives; a wizard already in AI mode can still switch back from there
    const aiObjectiveSupported = isAiAudienceObjective(selectedObjective)
    const showAiAudience = aiAudienceEnabled && (aiObjectiveSupported || builderState.mode === 'ai')
    // The plain reason an existing campaign is not offered AI audiences; nothing when the licence does not have them
    const addAudienceNotice =
        addAudienceEligibility && !addAudienceEligibility.eligible && addAudienceEligibility.reason !== 'disabled'
            ? addAudienceEligibility.message
            : null
    const aiAudiencesSelected = (audienceSelections.acquisition || []).some((id) => hypothesisMap[id])
    const builderInputs = {
        campaignId,
        publisherId: connectedPublishers ? Object.keys(connectedPublishers)[0] : undefined,
        objective: selectedObjective,
        location: getStepContentData('locationTargeting')?.selectedLocation || 'worldwide',
        ...(addAudienceCampaignId ? { adCampaignId: addAudienceCampaignId } : {}),
    }
    const aiAudiencesStale =
        aiAudiencesSelected &&
        isSetStale(
            builderState.set,
            withDonorInput(
                buildSetInputs({
                    ...builderInputs,
                    acquisitionBudget: parseBudgetAmount(builderState.acquisitionBudget) || 0,
                    requestedPersonas: builderState.requestedPersonas,
                }),
                salesFunnelData,
            ),
        )

    // The guard reads the latest state through a ref, so a guard registered earlier never judges outdated selections
    const guardState = useRef(null)
    guardState.current = {
        audienceSelections,
        availableStages,
        aiAudienceMode: aiAudienceEnabled && builderState.mode === 'ai',
        aiObjectiveSupported,
        aiAudiencesStale,
        budgetMoveMissing: !!addAudienceCampaignId && aiAudiencesSelected && !getBudgetMove(salesFunnelData),
    }

    useEffect(() => {
        const guard = () => {
            const current = guardState.current
            if (current.aiAudienceMode && !current.aiObjectiveSupported) {
                showNotification(
                    'AI audiences are not available for this campaign objective. Switch to the existing audiences to continue',
                    'error',
                    3000,
                    null,
                    null,
                    false,
                    true,
                )
                return false
            }

            // Only check audience selections for available stages
            const selectedAudiencesInAvailableStages = current.availableStages
                .map((stageId) => current.audienceSelections[stageId] || [])
                .flat()

            if (selectedAudiencesInAvailableStages.length === 0) {
                const message = current.aiAudienceMode
                    ? 'Build the AI audiences or switch to the existing audiences to continue'
                    : 'Please select at least one audience'
                showNotification(message, 'error', 3000, null, null, false, true)
                return false
            }

            if (current.budgetMoveMissing) {
                showNotification(
                    'Choose the ad set the budget of the AI audiences comes from, and the amount to move',
                    'error',
                    3000,
                    null,
                    null,
                    false,
                    true,
                )
                return false
            }

            if (current.aiAudiencesStale) {
                showNotification(
                    'Your campaign details changed. Regenerate the AI audiences to continue',
                    'error',
                    3000,
                    null,
                    null,
                    false,
                    true,
                )
                return false
            }

            return true
        }

        registerNextGuard(guard)

        return () => deregisterNextGuard(guard)
    }, [registerNextGuard, deregisterNextGuard])

    if (audiencesLoading)
        return (
            <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                <CubeLoader size="default" />
            </div>
        )

    const handleBuilderChange = (nextData) => {
        const selectionsChanged =
            JSON.stringify(nextData.audienceSelections) !== JSON.stringify(salesFunnelData.audienceSelections)
        setStepContentData(nextData, 'salesFunnelSetup')
        setAudienceSelections(nextData.audienceSelections || {})
        if (selectionsChanged) removeStepContentData('budgetSchedule')
    }

    // Lead form retargeting is offered in the retargeting stage; it is built from the lead forms, so it does not need the pixel
    const warmAudiencePanel = (
        <WarmAudiencePanel
            objective={selectedObjective}
            data={salesFunnelData}
            onChange={handleBuilderChange}
            isEditMode={getStepContentData('prefetchedData')?.isEditMode === true}
        />
    )

    // Lookalikes are offered in the acquisition stage when the server offers them for the licence
    const lookalike = {
        panel: (
            <LookalikePanel
                inputs={builderInputs}
                data={salesFunnelData}
                onChange={handleBuilderChange}
                isEditMode={getStepContentData('prefetchedData')?.isEditMode === true}
            />
        ),
        hiddenAudienceIds: getLookalikeMap(salesFunnelData),
    }

    const handleDataChange = (stageId, selectedAudienceIds) => {
        // Update local state for immediate UI refresh
        const updatedSelections = {
            ...audienceSelections,
            [stageId]: selectedAudienceIds,
        }

        setAudienceSelections(updatedSelections)
    }

    const showTour = () => {
        if (selectedObjective !== 'awareness') return false
        return showGuidePopups
    }

    const steps = [
        {
            target: '.reengagement-section',
            title: 'Step 1 of 3',
            content: (
                <TourFunneStep text="Reengagement sales funnel stage and it’s audiences are not available for Awareness campaign objective." />
            ),
            placement: 'top',
            disableBeacon: true,
            spotlightPadding: 11,
        },
        {
            target: '.retargeting-section',
            title: 'Step 2 of 3',
            content: (
                <TourFunneStep text="Retargeting sales funnel stage and it’s audiences are not available for Awareness campaign objective." />
            ),
            placement: 'top',
            disableBeacon: true,
            spotlightPadding: 11,
        },
        {
            target: '.retention-section',
            title: 'Step 3 of 3',
            content: (
                <TourFunneStep text="Retention sales funnel stage and it’s audiences are not available for Awareness campaign objective." />
            ),
            placement: 'top',
            disableBeacon: true,
            spotlightPadding: 11,
        },
    ]

    return (
        <>
            <Tour steps={steps} run={showTour()} />
            <div className="tw:flex tw:h-full">
                <div className="tw:pt-6 tw:pb-16 tw:flex-1 tw:overflow-y-auto">
                    <span className="tw:text-sm tw:px-6 tw:block">
                        This step allows you to select which sales funnel stages you want to target in your campaign.
                        You can choose one or more stages depending on your business goals, campaign objectives, and
                        customer journey stage.
                    </span>

                    <div className="tw:mt-6 tw:flex tw:gap-6 tw:flex-col">
                        {allStages.map((stage) => (
                            <Stage
                                key={stage.id}
                                color={stage.color}
                                name={stage.name}
                                funnelStageId={stage.id}
                                audiences={audiences}
                                availableStages={availableStages}
                                disabled={!availableStages.includes(stage.id)}
                                selectedItems={getSelectedAudiences(stage.id)}
                                showInfos={!showGuidePopups}
                                dataChanged={handleDataChange}
                                warmAudiencePanel={stage.id === 'retargeting' ? warmAudiencePanel : undefined}
                                accountAudiencePanel={
                                    <AccountAudiencesPanel
                                        stageId={stage.id}
                                        data={salesFunnelData}
                                        onChange={handleBuilderChange}
                                        isEditMode={getStepContentData('prefetchedData')?.isEditMode === true}
                                    />
                                }
                                lookalike={stage.id === 'acquisition' && !addAudienceCampaignId ? lookalike : undefined}
                                aiAudience={
                                    showAiAudience && stage.id === 'acquisition'
                                        ? {
                                              panel: (
                                                  <AiAudiencePanel
                                                      data={salesFunnelData}
                                                      onChange={handleBuilderChange}
                                                      inputs={builderInputs}
                                                      objectiveSupported={aiObjectiveSupported}
                                                      currency={
                                                          connectedPublishers
                                                              ? Object.values(connectedPublishers)[0]?.currency
                                                              : undefined
                                                      }
                                                      budgetMove={addAudienceCampaignId ? addAudienceEligibility : null}
                                                  />
                                              ),
                                              hiddenAudienceIds: builderState.mode === 'ai' ? hypothesisMap : null,
                                              showExistingAudiences:
                                                  builderState.mode !== 'ai' || builderState.advanced,
                                          }
                                        : addAudienceNotice && stage.id === 'acquisition'
                                          ? {
                                                panel: (
                                                    <AddAudienceNotOffered
                                                        message={addAudienceNotice}
                                                        unfinishedAdSets={unfinishedAdSets}
                                                        onRemove={removeUnfinishedAdSets}
                                                        removing={removingUnfinished}
                                                    />
                                                ),
                                                hiddenAudienceIds: null,
                                                showExistingAudiences: true,
                                            }
                                          : undefined
                                }
                            />
                        ))}
                    </div>
                </div>

                <div className="tw:border-l tw:px-10 tw:py-6 tw:w-fit tw:flex-none">
                    <Funnel availableStages={filteredAvailableStages} audienceSelections={audienceSelections} />
                </div>
            </div>
        </>
    )
}

export default SalesFunnelSetup
